import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { 
  Search, 
  Users, 
  TrendingUp, 
  Wallet, 
  ShieldCheck, 
  Download, 
  Loader2, 
  ChevronDown, 
  ChevronUp, 
  RotateCcw,
  Sparkles,
  ArrowUpRight,
  CreditCard,
  Building2,
  Calendar,
  Package,
  FileCheck2,
  FileText,
  BadgeCheck
} from 'lucide-react';

export default function ReportsAnalytics() {
  const [dataList, setDataList] = useState([]);
  const [stats, setStats] = useState({
    totalStudents: 0,
    totalAgreedFees: 0,
    totalCollectedFees: 0,
    activeLogins: 0,
    booksDispatched: 0,
    assignmentsSubmitted: 0
  });
  const [loading, setLoading] = useState(false);
  const [coursesList, setCoursesList] = useState([]);

  // Filter States
  const [searchTerm, setSearchTerm] = useState('');
  const [universityNumber, setUniversityNumber] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [selectedCourse, setSelectedCourse] = useState('ALL');
  const [selectedSemester, setSelectedSemester] = useState('ALL');

  // Expanded Row Tracking
  const [expandedStudentId, setExpandedStudentId] = useState(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  // 1. Fetch Dynamic Course Options
  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const res = await fetch('https://metapi.emedhatechnologies.com/api/courses/courses');
        const result = await res.json();
        if (result.success && result.data) {
          setCoursesList(result.data);
        }
      } catch (err) {
        console.error("Failed to load course list:", err);
      }
    };
    fetchCourses();
  }, []);

  // 2. Fetch Report Records
  useEffect(() => {
    fetchReportData();
    setCurrentPage(1);
  }, [searchTerm, universityNumber, fromDate, toDate, selectedCourse, selectedSemester]);

  const fetchReportData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        search: searchTerm,
        universityNumber: universityNumber,
        fromDate: fromDate,
        toDate: toDate,
        course: selectedCourse,
        semester: selectedSemester
      });

      const res = await fetch(`https://metapi.emedhatechnologies.com/api/students/student-reports?${params.toString()}`);
      const result = await res.json();

      if (result.success) {
        setDataList(result.data || []);
        setStats(result.stats || {
          totalStudents: 0,
          totalAgreedFees: 0,
          totalCollectedFees: 0,
          activeLogins: 0,
          booksDispatched: 0,
          assignmentsSubmitted: 0
        });
      } else {
        setDataList([]);
      }
    } catch (err) {
      console.error("Failed to fetch reports:", err);
      setDataList([]);
    } finally {
      setLoading(false);
    }
  };

  const handleResetFilters = () => {
    setSearchTerm('');
    setUniversityNumber('');
    setFromDate('');
    setToDate('');
    setSelectedCourse('ALL');
    setSelectedSemester('ALL');
  };

  // 3. Export to Excel Engine
  const handleExportExcel = () => {
    if (dataList.length === 0) {
      alert("No report records available to export for current criteria.");
      return;
    }

    const exportRows = dataList.map((item, index) => ({
      "S.No": index + 1,
      "Admission No": item.adm_number || `MET-ADM-${item.id}`,
      "Univ Reg No": item.university_adm_number || 'N/A',
      "Candidate Full Name": item.name || 'N/A',
      "Email Address": item.email || 'N/A',
      "Primary Mobile": item.mobile || 'N/A',
      "Alternative Mobile": item.alt_mobile || 'N/A',
      "Gender": item.gender || 'N/A',
      "Father Name": item.father_name || 'N/A',
      "Mother Name": item.mother_name || 'N/A',
      "DOB": item.dob ? String(item.dob).substring(0, 10) : 'N/A',
      "Residential Address": item.address || 'N/A',
      "Target Course Domain": item.target_course || 'N/A',
      "Campus / University": item.college_university || 'N/A',
      "Academic Year": item.academic_year || 'N/A',

      "Highest Qualification": item.highest_qualification || 'XII',
      "Total Agreed Fees (INR)": Number(item.total_agreed_fees || 0),
      "Total Paid Fees (INR)": Number(item.total_paid_amount || 0),
      "Portal Login Status": item.login_access_provisioned === 1 ? (item.is_account_active === 1 ? "Active" : "Suspended") : "Pending",
      "Books Dispatches": item.books_count,
      "Assignments Logged": item.assignments_count,
      "Documents Uploaded": item.documents_count,
      "Admission Filed Date": item.created_at ? new Date(item.created_at).toLocaleDateString('en-IN') : 'N/A',
      "Onboarded By": item.created_by_name || 'Staff'
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Executive_Student_Analytics");

    const fileName = `Executive_Student_Analytics_${new Date().toISOString().substring(0, 10)}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = dataList.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(dataList.length / itemsPerPage);

  const collectionRate = stats.totalAgreedFees > 0 
    ? Math.min(100, Math.round((stats.totalCollectedFees / stats.totalAgreedFees) * 100)) 
    : 0;

  return (
    <div style={styles.container}>
      
     {/* ⚡ CLEAN & MINIMAL HEADER */}
<div style={styles.headerHeroCard}>
  <div>
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <h2 style={styles.pageTitle}>Reports & Analytics</h2>
      <span style={styles.liveIntelligencePill}>
        <span style={styles.pulsingDot}></span> Live
      </span>
    </div>
    <p style={styles.pageSubtitle}>
      Overview of student records, revenue tracking, and academic progress
    </p>
  </div>

  <button onClick={handleExportExcel} style={styles.luxuryExportBtn}>
    <Download size={14} /> Export Report (Excel)
  </button>
</div>

{/* ⚡ 4 KPI CARDS IN A SINGLE ROW (DESKTOP / LAPTOP) */}
<div style={styles.kpiGrid}>
  
  {/* Card 1: Total Candidates */}
  <div style={styles.luxuryKpiCard}>
    <div style={styles.kpiGlowBlue}></div>
    <div style={styles.kpiCardTop}>
      <span style={styles.kpiCategoryLabel}>Total Enrolled</span>
      <div style={{ ...styles.kpiIconBox, background: '#eff6ff', color: '#1d4ed8' }}>
        <Users size={16} />
      </div>
    </div>
    <div style={styles.kpiMainNumber}>{stats.totalStudents.toLocaleString('en-IN')}</div>
    <div style={styles.kpiFooterRow}>
      <span style={styles.kpiBadgeBlue}>Active Cohort</span>
      <span style={styles.kpiMutedHint}>Filtered Total</span>
    </div>
  </div>

  {/* Card 2: Total Agreed Fees */}
  <div style={styles.luxuryKpiCard}>
    <div style={styles.kpiGlowPurple}></div>
    <div style={styles.kpiCardTop}>
      <span style={styles.kpiCategoryLabel}>Agreed Fees</span>
      <div style={{ ...styles.kpiIconBox, background: '#f8fafc', color: '#0f172a' }}>
        <TrendingUp size={16} />
      </div>
    </div>
    <div style={styles.kpiMainNumber}>₹{Number(stats.totalAgreedFees).toLocaleString('en-IN')}</div>
    <div style={styles.kpiFooterRow}>
      <span style={styles.kpiBadgeSlate}>Target Volume</span>
      <span style={styles.kpiMutedHint}>Commitment</span>
    </div>
  </div>

  {/* Card 3: Total Collected Revenue */}
  <div style={styles.luxuryKpiCard}>
    <div style={styles.kpiGlowEmerald}></div>
    <div style={styles.kpiCardTop}>
      <span style={styles.kpiCategoryLabel}>Fees Collected</span>
      <div style={{ ...styles.kpiIconBox, background: '#ecfdf5', color: '#047857' }}>
        <Wallet size={16} />
      </div>
    </div>
    <div style={{ ...styles.kpiMainNumber, color: '#047857' }}>
      ₹{Number(stats.totalCollectedFees).toLocaleString('en-IN')}
    </div>
    <div style={styles.kpiFooterRow}>
      <span style={styles.kpiBadgeEmerald}>{collectionRate}% Realized</span>
      <span style={styles.kpiMutedHint}>Settled Receipts</span>
    </div>
  </div>

  {/* Card 4: Active Portal Accounts */}
  <div style={styles.luxuryKpiCard}>
    <div style={styles.kpiGlowIndigo}></div>
    <div style={styles.kpiCardTop}>
      <span style={styles.kpiCategoryLabel}>ACTIVE Accounts</span>
      <div style={{ ...styles.kpiIconBox, background: '#eef2ff', color: '#4338ca' }}>
        <ShieldCheck size={16} />
      </div>
    </div>
    <div style={styles.kpiMainNumber}>{stats.activeLogins.toLocaleString('en-IN')}</div>
    <div style={styles.kpiFooterRow}>
      <span style={styles.kpiBadgeIndigo}>Verified Logins</span>
      <span style={styles.kpiMutedHint}>Active Logins</span>
    </div>
  </div>

</div>

      {/* ⚡ REFINED FILTER CONTROL DECK */}
      <div style={styles.controlDeckSurface}>
        
        {/* ROW 1: Search Inputs */}
        <div style={styles.searchRowGrid}>
          
          <div style={{ flex: 1.5, minWidth: '260px' }}>
            <span style={styles.microLabel}>Candidate Search</span>
            <div style={styles.searchBoxWrap}>
              <Search size={15} style={styles.searchIconLeading} />
              <input
                type="text"
                placeholder="Search by student name, mobile, email, adm no..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={styles.luxInputWithIcon}
              />
            </div>
          </div>

          <div style={{ flex: 1, minWidth: '220px' }}>
            <span style={styles.microLabel}>University Admission / Reg No</span>
            <div style={styles.searchBoxWrap}>
              <Building2 size={15} style={styles.searchIconLeading} />
              <input
                type="text"
                placeholder="e.g. AU-2026-REG-0145"
                value={universityNumber}
                onChange={(e) => setUniversityNumber(e.target.value)}
                style={styles.luxInputWithIcon}
              />
            </div>
          </div>

        </div>

        {/* ROW 2: Date Range, Course, Semester & Reset Button */}
        {/* ROW 2: Date Range, Enrolled Course Track (Wider), Semester & Reset Button */}
<div style={styles.controlsRowGrid}>
  
  <div style={styles.filterInputsCluster}>
    
    {/* Inline Date Range Picker */}
    <div style={styles.luxuryDateRangeBox}>
      <div style={styles.dateBlockWrap}>
        <span style={styles.dateMicroTag}>From</span>
        <input
          type="date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          style={styles.cleanDateInput}
        />
      </div>

      <span style={styles.dateSeparatorDivider}>—</span>

      <div style={styles.dateBlockWrap}>
        <span style={styles.dateMicroTag}>To</span>
        <input
          type="date"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          style={styles.cleanDateInput}
        />
      </div>
    </div>

    {/* Enrolled Course Track (Wider Flexible Width) */}
    <div style={styles.courseSelectBlock}>
      <span style={styles.microLabel}>Enrolled Course Track</span>
      <select
        value={selectedCourse}
        onChange={(e) => setSelectedCourse(e.target.value)}
        style={styles.luxSelectInputCourse}
      >
        <option value="ALL">All Enrolled Courses</option>
        {coursesList.map((c) => (
          <option key={c.id} value={c.course_name.trim()}>
            {c.course_name.trim()}
          </option>
        ))}
      </select>
    </div>

    {/* Term / Semester */}
    <div style={styles.semesterSelectBlock}>
      <span style={styles.microLabel}>Term / Semester</span>
      <select
        value={selectedSemester}
        onChange={(e) => setSelectedSemester(e.target.value)}
        style={styles.luxSelectInputSemester}
      >
        <option value="ALL">All Semesters / Years</option>
        <option value="Semester 1">Semester 1</option>
        <option value="Semester 2">Semester 2</option>
        <option value="Semester 3">Semester 3</option>
        <option value="Semester 4">Semester 4</option>
        <option value="Semester 5">Semester 5</option>
        <option value="Semester 6">Semester 6</option>
        <option value="Year 1">Year 1</option>
        <option value="Year 2">Year 2</option>
        <option value="Year 3">Year 3</option>
      </select>
    </div>

  </div>

  {/* Reset Active Filters Button */}
  {(searchTerm || universityNumber || fromDate || toDate || selectedCourse !== 'ALL' || selectedSemester !== 'ALL') && (
    <button type="button" onClick={handleResetFilters} style={styles.resetFiltersBtn}>
      <RotateCcw size={13} /> Reset Filters
    </button>
  )}

</div>

      </div>

      {/* ⚡ MASTER ANALYTICAL DATA TABLE */}
      {loading ? (
        <div style={styles.loadingContainer}>
          <Loader2 size={36} style={{ animation: 'spin 1s linear infinite' }} color="#2563eb" />
          <span style={styles.loadingText}>Synthesizing student records...</span>
        </div>
      ) : (
        <div style={styles.tableSuperContainer}>
          <div style={{ overflowX: 'auto', width: '100%' }}>
            <table style={styles.luxTable}>
              <thead>
                <tr>
                  <th style={styles.luxTh}>Student & Univ ID</th>
                  <th style={styles.luxTh}>Candidate Profile</th>
                  <th style={styles.luxTh}>Course Stream</th>
                  <th style={styles.luxTh}>Center / Campus</th>
                  {/* <th style={styles.luxTh}>Current Term</th> */}
                  <th style={styles.luxTh}>Agreed Fee</th>
                  <th style={styles.luxTh}>Fee Collected</th>
                  <th style={styles.luxTh}>Dossier Stats</th>
                  <th style={{ ...styles.luxTh, textAlign: 'center' }}>Breakdown</th>
                </tr>
              </thead>
              <tbody>
                {dataList.length === 0 ? (
                  <tr>
                    <td colSpan="9" style={styles.emptyTableTd}>
                      No student records found matching the active analytical parameters.
                    </td>
                  </tr>
                ) : (
                  currentItems.map((std) => {
                    const isExpanded = expandedStudentId === std.id;
                    return (
                      <React.Fragment key={std.id}>
                        <tr style={isExpanded ? styles.luxTrActive : styles.luxTr}>
                          
                          {/* Admission Numbers */}
                          <td style={styles.luxTd}>
                            <div style={styles.admNoHighlight}>
                              {std.adm_number || `MET-ADM-${std.id}`}
                            </div>
                            <div style={styles.univRegSmall}>
                              Univ: <strong>{std.university_adm_number || 'Pending'}</strong>
                            </div>
                          </td>

                          {/* Candidate Details */}
                          <td style={styles.luxTd}>
                            <div style={styles.studentNameText}>{std.name}</div>
                            <div style={styles.studentSubContact}>{std.email}</div>
                            <div style={styles.studentMobileContact}>{std.mobile}</div>
                          </td>

                          {/* Course Track */}
                          <td style={styles.luxTd}>
                            <span style={styles.luxuryCourseBadge}>{std.target_course}</span>
                          </td>

                          {/* Campus */}
                          <td style={styles.luxTd}>
                            <div style={styles.campusLocationText}>
                              {std.college_university}
                            </div>
                          </td>

                          {/* Term & Year */}
                          {/* <td style={styles.luxTd}>
                            <div style={styles.termDisplayText}>{std.current_semester}</div>
                            <div style={styles.ayDisplayText}>AY: {std.academic_year}</div>
                          </td> */}

                          {/* Agreed Fee */}
                          <td style={{ ...styles.luxTd, fontWeight: '700', color: '#0f172a' }}>
                            ₹{Number(std.total_agreed_fees).toLocaleString('en-IN')}
                          </td>

                          {/* Collected Fee */}
                          <td style={{ ...styles.luxTd, fontWeight: '800', color: '#047857' }}>
                            ₹{Number(std.total_paid_amount).toLocaleString('en-IN')}
                          </td>

                          {/* Dossier Micro Tags */}
                          <td style={styles.luxTd}>
                            <div style={styles.dossierTagsWrap}>
                              <span style={styles.microDossierPill} title="Books Dispatches Recorded">
                                📦 {std.books_count}
                              </span>
                              <span style={styles.microDossierPill} title="Assignments Recorded">
                                📝 {std.assignments_count}
                              </span>
                              <span style={styles.microDossierPill} title="Documents Stored">
                                📄 {std.documents_count}
                              </span>
                            </div>
                          </td>

                          {/* Expand Breakdown Toggle */}
                          <td style={{ ...styles.luxTd, textAlign: 'center' }}>
                            <button
                              onClick={() => setExpandedStudentId(isExpanded ? null : std.id)}
                              style={{
                                ...styles.expandRowTriggerBtn,
                                background: isExpanded ? '#0f172a' : '#f8fafc',
                                color: isExpanded ? '#ffffff' : '#334155',
                                borderColor: isExpanded ? '#0f172a' : '#e2e8f0'
                              }}
                              title="Toggle comprehensive dossier"
                            >
                              {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                            </button>
                          </td>

                        </tr>

                        {/* ⚡ EXPANDED DOSSIER SUB-WORKSPACE */}
                        {isExpanded && (
                          <tr style={{ background: '#f8fafc' }}>
                            <td colSpan="9" style={styles.dossierExpandedTd}>
                              <div style={styles.dossierWorkspaceDeck}>
                                
                                {/* 1. Payment Receipts Ledger */}
                                <div style={styles.dossierInnerCard}>
                                  <div style={styles.dossierCardHeader}>
                                    <div style={styles.dossierHeaderLeft}>
                                      <CreditCard size={14} color="#2563eb" />
                                      <strong style={styles.dossierCardTitle}>Fee Receipts ({std.payments.length})</strong>
                                    </div>
                                    <span style={styles.dossierHeaderBadge}>Total Paid: ₹{Number(std.total_paid_amount).toLocaleString('en-IN')}</span>
                                  </div>
                                  {std.payments.length === 0 ? (
                                    <span style={styles.emptyDossierNote}>No fee transaction receipts cleared.</span>
                                  ) : (
                                    <div style={styles.dossierScrollList}>
                                      {std.payments.map((p) => (
                                        <div key={p.id} style={styles.dossierItemRow}>
                                          <div>
                                            <div style={styles.itemTitle}>{p.receipt_number}</div>
                                            <div style={styles.itemSub}>{p.fee_type} • {p.academic_term} ({p.payment_mode})</div>
                                          </div>
                                          <div style={styles.itemAmountHighlight}>
                                            ₹{Number(p.amount_paid_now).toLocaleString('en-IN')}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>

                                {/* 2. Book Dispatches */}
                                <div style={styles.dossierInnerCard}>
                                  <div style={styles.dossierCardHeader}>
                                    <div style={styles.dossierHeaderLeft}>
                                      <Package size={14} color="#d97706" />
                                      <strong style={styles.dossierCardTitle}>Book Dispatches ({std.books.length})</strong>
                                    </div>
                                    <span style={styles.dossierHeaderBadge}>Shipments</span>
                                  </div>
                                  {std.books.length === 0 ? (
                                    <span style={styles.emptyDossierNote}>No material dispatches recorded.</span>
                                  ) : (
                                    <div style={styles.dossierScrollList}>
                                      {std.books.map((b) => (
                                        <div key={b.id} style={styles.dossierItemRow}>
                                          <div>
                                            <div style={styles.itemTitle}>{b.term_label}</div>
                                            <div style={styles.itemSub}>{b.courier_details || 'Hand Delivery'}</div>
                                          </div>
                                          <div style={styles.itemDateSmall}>{b.sent_date ? String(b.sent_date).substring(0, 10) : '-'}</div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>

                                {/* 3. Assignment Submissions */}
                                <div style={styles.dossierInnerCard}>
                                  <div style={styles.dossierCardHeader}>
                                    <div style={styles.dossierHeaderLeft}>
                                      <FileCheck2 size={14} color="#059669" />
                                      <strong style={styles.dossierCardTitle}>Assignments ({std.assignments.length})</strong>
                                    </div>
                                    <span style={styles.dossierHeaderBadge}>Academic Track</span>
                                  </div>
                                  {std.assignments.length === 0 ? (
                                    <span style={styles.emptyDossierNote}>No assignment submissions logged.</span>
                                  ) : (
                                    <div style={styles.dossierScrollList}>
                                      {std.assignments.map((a) => (
                                        <div key={a.id} style={styles.dossierItemRow}>
                                          <div>
                                            <div style={styles.itemTitle}>{a.project_title || 'Term Project'}</div>
                                            <div style={styles.itemSub}>{a.term_label}</div>
                                          </div>
                                          <span style={{ 
                                            fontSize: '11px', 
                                            fontWeight: '700', 
                                            color: a.is_completed === 1 ? '#047857' : '#b45309' 
                                          }}>
                                            {a.is_completed === 1 ? '✓ Submitted' : 'Pending'}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>

                                {/* 4. Stored Documents */}
                                <div style={styles.dossierInnerCard}>
                                  <div style={styles.dossierCardHeader}>
                                    <div style={styles.dossierHeaderLeft}>
                                      <FileText size={14} color="#4f46e5" />
                                      <strong style={styles.dossierCardTitle}>Documents ({std.documents.length})</strong>
                                    </div>
                                    <span style={styles.dossierHeaderBadge}>KYC Vault</span>
                                  </div>
                                  {std.documents.length === 0 ? (
                                    <span style={styles.emptyDossierNote}>No digital verification files attached.</span>
                                  ) : (
                                    <div style={styles.dossierScrollList}>
                                      {std.documents.map((d) => (
                                        <div key={d.id} style={styles.dossierItemRow}>
                                          <div>
                                            <div style={styles.itemTitle}>{d.name}</div>
                                            <div style={styles.itemSub}>{d.type}</div>
                                          </div>
                                          <span style={styles.docStatusBadge}>
                                            {d.status || 'Verified'}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>

                              </div>
                            </td>
                          </tr>
                        )}

                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* ⚡ LUXURY PAGINATION TOOLBAR */}
          {dataList.length > 0 && (
            <div style={styles.luxuryPaginationBar}>
              <span style={styles.paginationCountText}>
                Showing <strong>{indexOfFirstItem + 1}</strong>–<strong>{Math.min(indexOfLastItem, dataList.length)}</strong> of <strong>{dataList.length}</strong> Candidate Records
              </span>
              <div style={styles.pagButtonGroup}>
                <button
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                  style={currentPage === 1 ? { ...styles.pagActionBtn, ...styles.pagActionBtnDisabled } : styles.pagActionBtn}
                >
                  Previous
                </button>
                <span style={styles.activePageBadge}>Page {currentPage} of {totalPages || 1}</span>
                <button
                  disabled={currentPage === totalPages || totalPages === 0}
                  onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                  style={currentPage === totalPages || totalPages === 0 ? { ...styles.pagActionBtn, ...styles.pagActionBtnDisabled } : styles.pagActionBtn}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Embedded Spinner Keyframes */}
      <style dangerouslySetInnerHTML={{__html: `
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
      `}} />

    </div>
  );
}

// ⚡ LUXURY SaaS DESIGN SYSTEM STYLES
const styles = {
  container: {
    width: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  },
  
  // Hero Header
  headerHeroCard: {
    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
    borderRadius: '18px',
    padding: '24px 28px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '20px',
    flexWrap: 'wrap',
    boxShadow: '0 10px 25px -5px rgba(15, 23, 42, 0.25)',
    border: '1px solid rgba(255, 255, 255, 0.08)'
  },
  heroTextSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px'
  },
  brandTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flexWrap: 'wrap'
  },
  sparkleIconBox: {
    width: '28px',
    height: '28px',
    borderRadius: '8px',
    background: 'rgba(56, 189, 248, 0.15)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  pageTitle: {
    margin: 0,
    fontSize: '22px',
    fontWeight: '800',
    color: '#ffffff',
    letterSpacing: '-0.4px'
  },
  liveIntelligencePill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    background: 'rgba(16, 185, 129, 0.15)',
    border: '1px solid rgba(52, 211, 153, 0.3)',
    color: '#34d399',
    padding: '3px 10px',
    borderRadius: '20px',
    fontSize: '11px',
    fontWeight: '700'
  },
  pulsingDot: {
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    background: '#34d399'
  },
  pageSubtitle: {
    margin: 0,
    fontSize: '13px',
    color: '#94a3b8',
    maxWidth: '620px',
    lineHeight: 1.5
  },
  luxuryExportBtn: {
    background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
    color: '#ffffff',
    border: 'none',
    borderRadius: '10px',
    padding: '12px 20px',
    fontSize: '13px',
    fontWeight: '700',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    boxShadow: '0 4px 16px rgba(37, 99, 235, 0.35)',
    transition: 'all 0.2s ease'
  },

  // KPI Deck
  kpiGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))',
    gap: '16px'
  },
  luxuryKpiCard: {
    background: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: '16px',
    padding: '18px 20px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.02), 0 10px 15px -3px rgba(0,0,0,0.02)',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    position: 'relative',
    overflow: 'hidden'
  },
  kpiGlowBlue: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '3px',
    background: 'linear-gradient(90deg, #3b82f6 0%, #1d4ed8 100%)'
  },
  kpiGlowPurple: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '3px',
    background: 'linear-gradient(90deg, #0f172a 0%, #475569 100%)'
  },
  kpiGlowEmerald: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '3px',
    background: 'linear-gradient(90deg, #10b981 0%, #047857 100%)'
  },
  kpiGlowIndigo: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '3px',
    background: 'linear-gradient(90deg, #6366f1 0%, #4338ca 100%)'
  },
  kpiCardTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  kpiCategoryLabel: {
    fontSize: '11px',
    fontWeight: '700',
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  kpiIconBox: {
    width: '34px',
    height: '34px',
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 2px 6px rgba(0,0,0,0.08)'
  },
  kpiMainNumber: {
    fontSize: '24px',
    fontWeight: '800',
    color: '#0f172a',
    letterSpacing: '-0.5px',
    marginTop: '2px'
  },
  kpiFooterRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginTop: '4px'
  },
  kpiBadgeBlue: {
    fontSize: '10.5px',
    fontWeight: '700',
    padding: '2px 8px',
    borderRadius: '6px',
    background: '#eff6ff',
    color: '#1d4ed8'
  },
  kpiBadgeSlate: {
    fontSize: '10.5px',
    fontWeight: '700',
    padding: '2px 8px',
    borderRadius: '6px',
    background: '#f1f5f9',
    color: '#475569'
  },
  kpiBadgeEmerald: {
    fontSize: '10.5px',
    fontWeight: '700',
    padding: '2px 8px',
    borderRadius: '6px',
    background: '#ecfdf5',
    color: '#047857'
  },
  kpiBadgeIndigo: {
    fontSize: '10.5px',
    fontWeight: '700',
    padding: '2px 8px',
    borderRadius: '6px',
    background: '#eef2ff',
    color: '#4338ca'
  },
  kpiMutedHint: {
    fontSize: '11px',
    color: '#94a3b8'
  },

  // Control Deck
  controlDeckSurface: {
    background: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: '16px',
    padding: '18px 20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.02)'
  },
  filterTierRow1: {
    display: 'flex',
    gap: '16px',
    flexWrap: 'wrap',
    alignItems: 'center'
  },
  searchRowGrid: {
    display: 'flex',
    gap: '16px',
    flexWrap: 'wrap',
    alignItems: 'center'
  },
  controlsRowGrid: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '14px',
    flexWrap: 'wrap',
    paddingTop: '14px',
    borderTop: '1px dashed #edf2f7'
  },
  filterInputsCluster: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    flexWrap: 'wrap'
  },
  microLabel: {
    fontSize: '9.5px',
    fontWeight: '700',
    color: '#94a3b8',
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
    display: 'block',
    marginBottom: '4px'
  },
  searchBoxWrap: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    width: '100%'
  },
  searchIconLeading: {
    position: 'absolute',
    left: '12px',
    color: '#94a3b8'
  },
  luxInputWithIcon: {
    width: '100%',
    padding: '10px 12px 10px 38px',
    background: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '10px',
    color: '#0f172a',
    fontSize: '13px',
    fontWeight: '500',
    outline: 'none',
    boxSizing: 'border-box'
  },
  luxuryDateRangeBox: {
    display: 'inline-flex',
    alignItems: 'center',
    background: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '10px',
    padding: '5px 12px',
    gap: '8px'
  },
  dateBlockWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px'
  },
  dateMicroTag: {
    fontSize: '8.5px',
    fontWeight: '800',
    color: '#94a3b8',
    textTransform: 'uppercase',
    letterSpacing: '0.3px'
  },
  cleanDateInput: {
    border: 'none',
    background: 'transparent',
    color: '#0f172a',
    fontSize: '12px',
    fontWeight: '600',
    outline: 'none',
    cursor: 'pointer'
  },
  dateSeparatorDivider: {
    color: '#cbd5e1',
    fontWeight: '700',
    padding: '0 2px'
  },
  selectBlockWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px'
  },
  luxSelectInput: {
    background: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '10px',
    padding: '9px 12px',
    color: '#0f172a',
    fontSize: '12.5px',
    fontWeight: '500',
    outline: 'none',
    minWidth: '180px',
    cursor: 'pointer'
  },
  resetFiltersBtn: {
    background: '#fff1f2',
    border: '1px solid #fecaca',
    color: '#b91c1c',
    padding: '8px 14px',
    borderRadius: '8px',
    fontSize: '12px',
    fontWeight: '700',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    marginLeft: 'auto'
  },

  // Table Structure
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 20px',
    gap: '12px'
  },
  loadingText: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#64748b'
  },
  tableSuperContainer: {
    background: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(0,0,0,0.02)'
  },
  luxTable: {
    width: '100%',
    borderCollapse: 'collapse',
    textAlign: 'left'
  },
  luxTh: {
    padding: '14px 16px',
    borderBottom: '2px solid #e2e8f0',
    background: '#f8fafc',
    color: '#64748b',
    fontSize: '11px',
    textTransform: 'uppercase',
    letterSpacing: '0.6px',
    fontWeight: '700',
    whiteSpace: 'nowrap'
  },
  luxTr: {
    borderBottom: '1px solid #f1f5f9',
    transition: 'background 0.15s ease'
  },
  luxTrActive: {
    borderBottom: '1px solid #e2e8f0',
    background: '#eff6ff'
  },
  luxTd: {
    padding: '13px 16px',
    color: '#475569',
    fontSize: '13px',
    verticalAlign: 'middle',
    whiteSpace: 'nowrap'
  },
  emptyTableTd: {
    padding: '45px 16px',
    textAlign: 'center',
    color: '#94a3b8',
    fontSize: '14px',
    fontWeight: '500'
  },
  admNoHighlight: {
    fontWeight: '800',
    color: '#1e40af'
  },
  univRegSmall: {
    fontSize: '11px',
    color: '#64748b',
    marginTop: '2px'
  },
  studentNameText: {
    fontWeight: '700',
    color: '#0f172a'
  },
  studentSubContact: {
    fontSize: '11px',
    color: '#64748b'
  },
  studentMobileContact: {
    fontSize: '11px',
    color: '#334155',
    fontWeight: '600'
  },
  luxuryCourseBadge: {
    background: '#eff6ff',
    border: '1px solid #bfdbfe',
    color: '#1d4ed8',
    padding: '4px 10px',
    borderRadius: '8px',
    fontSize: '12px',
    fontWeight: '600',
    display: 'inline-block'
  },
  campusLocationText: {
    fontSize: '12.5px',
    color: '#334155',
    fontWeight: '600'
  },
  termDisplayText: {
    fontWeight: '700',
    color: '#0f172a'
  },
  ayDisplayText: {
    fontSize: '11px',
    color: '#64748b'
  },
  dossierTagsWrap: {
    display: 'flex',
    gap: '4px',
    flexWrap: 'wrap'
  },
  microDossierPill: {
    background: '#f1f5f9',
    color: '#334155',
    padding: '2px 6px',
    borderRadius: '4px',
    fontSize: '10.5px',
    fontWeight: '600'
  },
  expandRowTriggerBtn: {
    border: '1px solid #e2e8f0',
    width: '28px',
    height: '28px',
    borderRadius: '6px',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.15s ease'
  },

  // Dossier Sub-Workspace
  dossierExpandedTd: {
    padding: '18px 24px',
    borderBottom: '2px solid #e2e8f0',
    background: '#f8fafc'
  },
  dossierWorkspaceDeck: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))',
    gap: '14px'
  },
  dossierInnerCard: {
    background: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: '12px',
    padding: '12px 14px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    boxShadow: '0 1px 2px rgba(0,0,0,0.02)'
  },
  dossierCardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: '1px dashed #f1f5f9',
    paddingBottom: '8px'
  },
  dossierHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px'
  },
  dossierCardTitle: {
    fontSize: '12px',
    color: '#0f172a',
    fontWeight: '700'
  },
  dossierHeaderBadge: {
    fontSize: '10px',
    fontWeight: '700',
    color: '#64748b'
  },
  dossierScrollList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    maxHeight: '130px',
    overflowY: 'auto'
  },
  dossierItemRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: '11.5px',
    borderBottom: '1px solid #f8fafc',
    paddingBottom: '4px'
  },
  itemTitle: {
    fontWeight: '700',
    color: '#0f172a'
  },
  itemSub: {
    fontSize: '10px',
    color: '#64748b'
  },
  itemAmountHighlight: {
    fontWeight: '800',
    color: '#047857'
  },
  itemDateSmall: {
    fontSize: '10.5px',
    color: '#64748b'
  },
  emptyDossierNote: {
    fontSize: '11px',
    color: '#94a3b8',
    fontStyle: 'italic',
    padding: '8px 0'
  },
  docStatusBadge: {
    fontSize: '10px',
    fontWeight: '700',
    color: '#047857',
    background: '#ecfdf5',
    padding: '2px 6px',
    borderRadius: '4px'
  },

  // Pagination Toolbar
  luxuryPaginationBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '14px 20px',
    borderTop: '1px solid #edf2f7',
    background: '#fafbfc'
  },
  paginationCountText: {
    fontSize: '12px',
    color: '#64748b'
  },
  pagButtonGroup: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center'
  },
  pagActionBtn: {
    background: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    padding: '6px 14px',
    color: '#334155',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.15s ease'
  },
  pagActionBtnDisabled: {
    background: '#f8fafc',
    color: '#cbd5e1',
    cursor: 'not-allowed',
    borderColor: '#e2e8f0'
  },
  activePageBadge: {
    fontSize: '12px',
    fontWeight: '700',
    color: '#0f172a',
    padding: '0 4px'
  },
  headerHeroCard: {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '16px',
  flexWrap: 'wrap',
  paddingBottom: '4px'
},
pageTitle: {
  margin: 0,
  fontSize: '20px',
  fontWeight: '800',
  color: '#0f172a',
  letterSpacing: '-0.3px'
},
pageSubtitle: {
  margin: '2px 0 0 0',
  fontSize: '12.5px',
  color: '#64748b'
},
liveIntelligencePill: {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '5px',
  background: '#ecfdf5',
  border: '1px solid #a7f3d0',
  color: '#047857',
  padding: '2px 8px',
  borderRadius: '12px',
  fontSize: '11px',
  fontWeight: '700'
},
pulsingDot: {
  width: '6px',
  height: '6px',
  borderRadius: '50%',
  background: '#10b981'
},
luxuryExportBtn: {
  background: '#0f172a',
  color: '#ffffff',
  border: 'none',
  borderRadius: '8px',
  padding: '9px 16px',
  fontSize: '12.5px',
  fontWeight: '700',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  boxShadow: '0 2px 4px rgba(15, 23, 42, 0.1)',
  transition: 'all 0.15s ease'
},
kpiGrid: {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
  gap: '14px',
  width: '100%'
},
luxuryKpiCard: {
  background: '#ffffff',
  border: '1px solid #e2e8f0',
  borderRadius: '12px',
  padding: '14px 16px',
  boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  position: 'relative',
  overflow: 'hidden'
},
kpiGlowBlue: {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  height: '3px',
  background: '#2563eb'
},
kpiGlowPurple: {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  height: '3px',
  background: '#0f172a'
},
kpiGlowEmerald: {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  height: '3px',
  background: '#059669'
},
kpiGlowIndigo: {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  height: '3px',
  background: '#4f46e5'
},
kpiCardTop: {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center'
},
kpiCategoryLabel: {
  fontSize: '10.5px',
  fontWeight: '700',
  color: '#64748b',
  textTransform: 'uppercase',
  letterSpacing: '0.4px'
},
kpiIconBox: {
  width: '30px',
  height: '30px',
  borderRadius: '8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
},
kpiMainNumber: {
  fontSize: '20px',
  fontWeight: '800',
  color: '#0f172a',
  letterSpacing: '-0.3px',
  marginTop: '2px'
},
kpiFooterRow: {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  marginTop: '2px'
},
kpiBadgeBlue: {
  fontSize: '10px',
  fontWeight: '700',
  padding: '2px 6px',
  borderRadius: '4px',
  background: '#eff6ff',
  color: '#1d4ed8'
},
kpiBadgeSlate: {
  fontSize: '10px',
  fontWeight: '700',
  padding: '2px 6px',
  borderRadius: '4px',
  background: '#f1f5f9',
  color: '#475569'
},
kpiBadgeEmerald: {
  fontSize: '10px',
  fontWeight: '700',
  padding: '2px 6px',
  borderRadius: '4px',
  background: '#ecfdf5',
  color: '#047857'
},
kpiBadgeIndigo: {
  fontSize: '10px',
  fontWeight: '700',
  padding: '2px 6px',
  borderRadius: '4px',
  background: '#eef2ff',
  color: '#4338ca'
},
kpiMutedHint: {
  fontSize: '10.5px',
  color: '#94a3b8'
},
controlsRowGrid: {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-end',
  gap: '14px',
  flexWrap: 'wrap',
  paddingTop: '14px',
  borderTop: '1px dashed #edf2f7'
},
filterInputsCluster: {
  display: 'flex',
  alignItems: 'flex-end',
  gap: '12px',
  flexWrap: 'wrap',
  flex: 1
},
luxuryDateRangeBox: {
  display: 'inline-flex',
  alignItems: 'center',
  background: '#f8fafc',
  border: '1px solid #e2e8f0',
  borderRadius: '10px',
  padding: '6px 12px',
  gap: '8px',
  height: '38px',
  boxSizing: 'border-box'
},
courseSelectBlock: {
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
  flex: 1.8,
  minWidth: '240px',
  maxWidth: '360px'
},
semesterSelectBlock: {
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
  flex: 1,
  minWidth: '170px',
  maxWidth: '220px'
},
luxSelectInputCourse: {
  width: '100%',
  height: '38px',
  background: '#f8fafc',
  border: '1px solid #e2e8f0',
  borderRadius: '10px',
  padding: '0 12px',
  color: '#0f172a',
  fontSize: '12.5px',
  fontWeight: '500',
  outline: 'none',
  cursor: 'pointer',
  boxSizing: 'border-box'
},
luxSelectInputSemester: {
  width: '100%',
  height: '38px',
  background: '#f8fafc',
  border: '1px solid #e2e8f0',
  borderRadius: '10px',
  padding: '0 12px',
  color: '#0f172a',
  fontSize: '12.5px',
  fontWeight: '500',
  outline: 'none',
  cursor: 'pointer',
  boxSizing: 'border-box'
},
resetFiltersBtn: {
  background: '#fff1f2',
  border: '1px solid #fecaca',
  color: '#b91c1c',
  padding: '0 14px',
  height: '38px',
  borderRadius: '8px',
  fontSize: '12px',
  fontWeight: '700',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  boxSizing: 'border-box',
  whiteSpace: 'nowrap'
}

};